import { useEffect, useState } from "react";
import { Box, Button, Dialog, IconButton, Pagination, Spinner, Text } from "@primer/react";
import { formatDistanceToNow } from "date-fns";
import { CreditCardIcon, DownloadIcon, SyncIcon, TagIcon, XIcon } from "@primer/octicons-react";
import { useWalletTransactionsApi } from "../hooks/useWalletTransactionsApi";

interface TransactionHistoryProps {
  onClose: () => void;
  isOpen: boolean;
}

const getTransactionIcon = (source: string) => {
  if (source === "polar_payment") return <CreditCardIcon size={16} />;
  if (source === "cash_token") return <TagIcon size={16} />;
  return <DownloadIcon size={16} />;
};

const getRelativeDate = (paymentDate?: Date | string, createdDate?: Date | string) => {
  const value = paymentDate || createdDate;
  if (!value) return "Date unavailable";
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? "Date unavailable" : formatDistanceToNow(date, { addSuffix: true });
};

export function TransactionHistory({ onClose, isOpen }: TransactionHistoryProps) {
  const { transactions, pagination, loading, error, loadPage, refresh } = useWalletTransactionsApi(isOpen);
  const [loadingPage, setLoadingPage] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add("app-modal-open");
    return () => document.body.classList.remove("app-modal-open");
  }, [isOpen]);

  const handlePageChange = async (page: number) => {
    setLoadingPage(true);
    await loadPage(page);
    setLoadingPage(false);
  };

  const handleRefresh = async () => {
    setLoadingPage(true);
    await refresh();
    setLoadingPage(false);
  };

  return (
    <Dialog isOpen={isOpen} onDismiss={onClose} aria-labelledby="transaction-history-title" sx={{ maxWidth: "720px" }} className="transaction-history-dialog">
      <Dialog.Header>
        <Box className="transaction-history-header">
          <Text as="h2" id="transaction-history-title">Transaction history</Text>
          <Box className="transaction-history-header-actions">
            <IconButton icon={SyncIcon} aria-label="Refresh transactions" onClick={handleRefresh} disabled={loadingPage} variant="invisible" size="small" />
            <IconButton icon={XIcon} aria-label="Close transaction history" title="Close" onClick={onClose} variant="invisible" size="small" />
          </Box>
        </Box>
      </Dialog.Header>

      <Box className="transaction-history-content">
        {loading && !transactions.length ? (
          <Box className="transaction-history-state"><Spinner size="large" /><Text>Loading transactions…</Text></Box>
        ) : error ? (
          <Box className="transaction-history-state is-error">
            <Text as="h3">We couldn’t load your transactions</Text>
            <Text>{error}</Text>
            <Button variant="default" onClick={handleRefresh} disabled={loadingPage} leadingVisual={loadingPage ? Spinner : SyncIcon}>{loadingPage ? "Retrying…" : "Try again"}</Button>
          </Box>
        ) : !transactions.length ? (
          <Box className="transaction-history-state"><Text as="h3">No transactions yet</Text><Text>Payments and token redemptions will appear here.</Text></Box>
        ) : (
          <>
            <Box className="transaction-list">
              {transactions.map((transaction) => {
                const credited = transaction.type === "credit";
                return (
                  <Box className="transaction-row" key={transaction.id}>
                    <Box className={credited ? "transaction-row-icon is-credit" : "transaction-row-icon"}>{getTransactionIcon(transaction.source)}</Box>
                    <Box className="transaction-row-copy">
                      <Text as="strong">{credited ? "Added" : "Used"} {transaction.tokens.toLocaleString()} tokens</Text>
                      <Text>{transaction.source === "polar_payment" ? "Credit card" : transaction.source === "cash_token" ? "Cash token" : "Token activity"} · {getRelativeDate(transaction.payment_transaction_date, transaction.created_at)}</Text>
                    </Box>
                    <Box className="transaction-row-amount">
                      <Text as="strong" className={credited ? "is-credit" : "is-debit"}>{credited ? "+" : "-"}${transaction.amount.toFixed(2)}</Text>
                      {transaction.payment_status && <span className={`transaction-status is-${transaction.payment_status}`}>{transaction.payment_status}</span>}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {pagination && pagination.total_pages > 1 && (
              <Box className="transaction-pagination">
                <Pagination pageCount={pagination.total_pages} currentPage={pagination.current_page} onPageChange={(_, page) => { void handlePageChange(page); }} showPages />
                <Text>Showing {transactions.length} of {pagination.total_count} transactions</Text>
              </Box>
            )}
          </>
        )}
      </Box>
    </Dialog>
  );
}
